import {
  motion,
  useSpring,
  useTransform,
  useViewportScroll,
} from "framer-motion";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useEffectOnce } from "react-use";
import styled, { css } from "styled-components";
import { ScrollContainerContext } from "../context/ScrollContainerContext";
import { TimelineAnimateScrollContext } from "../context/TimlineAnimateScrollContext";
import { useResizeObserver } from "../lib/hooks/useResizeObserver";
import { fill } from "../styles/utils";
import { Box } from "./Box";

interface SmoothScrollScrollerProps {}

const ScrollContainer = styled(motion.div)`
  ${fill("fixed")}
  height: auto;
  overflow: hidden;
  will-change: transform;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

export const SmoothScrollScroller: React.FC<SmoothScrollScrollerProps> = ({
  children,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // page scrollable height based on content length
  const [pageHeight, setPageHeight] = useState(0);

  const [initialLoad, setInitialLoad] = useState(true);

  const {
    state: { isAnimating },
  } = useContext(TimelineAnimateScrollContext);

  const { scrollY } = useViewportScroll(); // measures how many pixels user has scrolled vertically

  const transform = useTransform(scrollY, [0, pageHeight], [0, -pageHeight]);
  const physics = {
    damping: 15,
    mass: 0.27,
    stiffness: !isAnimating ? 55 : 500,
  }; // easing of smooth scroll
  const spring = useSpring(transform, physics); // apply easing to the negative scroll value

  // update scrollable height when browser is resizing
  const resizePageHeight = useCallback((entries) => {
    for (let entry of entries) {
      setPageHeight(entry.contentRect.height);
    }
  }, []);
  const { setScrollContainer } = useContext(ScrollContainerContext);

  useResizeObserver(scrollRef, resizePageHeight);

  useEffectOnce(() => {
    setScrollContainer(scrollRef);
  });

  useEffectOnce(() => {
    setInitialLoad(false);
  });

  return (
    <>
      <ScrollContainer
        ref={scrollRef}
        style={initialLoad || isAnimating ? { y: transform } : { y: spring }}
        id="scroll-container"
      >
        {children}
      </ScrollContainer>
      <Box height={pageHeight}></Box>
    </>
  );
};
